//Util_App.ts =================================================
import "../Util/Prototypes";

import * as Custom from "../Custom";
import * as GetSearchResults from "../Search/GetSearchResults";
import * as GraLay from "./GraLay";
import * as MainMap from "../MainMap";
import * as Search from "../Search/Search";
import * as SelectOnMap from "../Search/SelectOnMap";
import * as Settings from "../Settings";
import * as Util from "./Util";
import * as Util_App from "./Util_App";

//ESRI javascript API.
import Extent from "@arcgis/core/geometry/Extent";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import Point from "@arcgis/core/geometry/Point";
import Sublayer from "@arcgis/core/layers/support/Sublayer";

export interface iField {
   name: string;
   alias: string;
   value: any;
   url: string;
   formatType: string;
   dataType: string;
   accessLevel: number;
}
export const emptyField: iField = { name: "", alias: "", value: "", url: "", formatType: "", dataType: "string", accessLevel: 0 };

export interface iAlias {
   name: string;
   formatType: string;
   accessLevel: number;
}

export interface iLayerField {
   layerName: string;
   fieldName: string;
}

export interface iNearFeatures {
   distance: number;
   feature: __esri.Graphic;
}

export interface iOverlayfeatures {
   feature: __esri.Graphic;
   areaOrDistance: number;
}

//Add a margin to feature map extent.
export function addMarginToMBR(mbrIn: __esri.Extent, adjust?: boolean): __esri.Extent {
   let wd: number;
   let ht: number;

   let xMin: number = 0;
   let yMin: number = 0;
   let xMax: number = 1;
   let yMax: number = 1;

   if (mbrIn) {
      xMin = mbrIn.xmin;
      yMin = mbrIn.ymin;
      xMax = mbrIn.xmax;
      yMax = mbrIn.ymax;

      wd = xMax - xMin;
      ht = yMax - yMin;

      if (wd === 0) { //Points.
         xMin -= Util_App.feetToMeters(Settings.zoomToPointMarginDistance);
         yMin -= Util_App.feetToMeters(Settings.zoomToPointMarginDistance);
         xMax += Util_App.feetToMeters(Settings.zoomToPointMarginDistance);
         yMax += Util_App.feetToMeters(Settings.zoomToPointMarginDistance);

      } else { //Lines and polygons.
         const dx: number = wd * Settings.zoomToMarginPercent;
         const dy: number = ht * Settings.zoomToMarginPercent;
         let d: number = dx;
         if (dy < dx) {
            d = dy;
         }
         xMin -= d;
         xMax += d;
         yMin -= d;
         yMax += d;
      }

      //Adjust width or height to work around view padding bugs.
      if (adjust) {
         wd = xMax - xMin;
         ht = yMax - yMin;
         const ratio: number = ht / wd;

         let vWd: number = MainMap.activeView.width - Settings.leftPanelWidth;
         let vHt: number = MainMap.activeView.height;
         let vRatio: number = vHt / vWd;

         if (Util.windowWidth() > Settings.smallScreenWidth) {
            if (vRatio > ratio) {
               const newHt: number = (vHt * wd) / vWd;
               const dy: number = (newHt - ht) / 2;
               yMin -= dy;
               yMax += dy;
            }

         } else { //Small screens.
            vWd = MainMap.activeView.width;
            vHt = MainMap.activeView.height / 2;
            vRatio = vHt / vWd;

            if (vRatio < ratio) {
               const newWd: number = (vWd * ht) / vHt;
               const dx: number = (newWd - wd) / 2;
               xMin -= dx;
               xMax += dx;
            }

         }
      }
   }

   const mbr = new Extent({xmin: xMin, ymin: yMin, xmax: xMax, ymax: yMax, spatialReference: MainMap.activeView.spatialReference});
   return mbr;
}

//Convert area in square feet to formatted text.
export function areaToText(area: number): string {
   const areaFeet: number = Util.round(area);
   const areaAcre: number = Util.round((area / 43560), 2);
   const areaMile: number = Util.round((area / 27878400), 2);

   let areaText: string = areaFeet + " feet²";

   if (areaMile >= 1) {
      areaText = areaMile + " miles²";

   } else if (areaAcre >= 0.1) {
      areaText = areaAcre + " acres";
   }

   return areaText;
}

//See if all sublayers are polygons.
export function areSublayersAllPolygon(layer: __esri.MapImageLayer, sublayerIds: number[]): boolean {
   for (const sublayerId of sublayerIds) {
      for (const sublayer of layer.allSublayers) {
         if (sublayer.id === sublayerId) {
            const layerSpec = Util_App.getLayerSpec(sublayer.title, layer.id);
            if (layerSpec && layerSpec.geometryType && layerSpec.geometryType === "polygon") {
               break;
            } else {
               return false;
            }
         }
      }
   }
   return true;
}

//Build contains all the words sql query clause.
export function buildContainsAllWordsSQL(fieldName: string, operator: string, value: string): string {
   let sql: string = "";
   const like: string = " " + operator + " ";
   const textBoxWords = cleanAndSplit(value, 15); //Clean up users text.

   for (let i: number = 0; i < textBoxWords.length; i++) {
      const word: string = textBoxWords[i];
      if (sql !== "") {
         sql += " AND ";
      }
      const firstChar: string = word.charAt(0);
      const lastChar: string = word.charAt(word.length - 1);
      const startsWithNumber: boolean = firstChar.IsNumeric();
      const endsWithNumber: boolean = lastChar.IsNumeric();

      if (startsWithNumber && endsWithNumber) {
         if (i === 0) {
            sql += fieldName + like + "'" + word + " %'";

         } else if (i === (textBoxWords.length - 1)) {
            sql += fieldName + like + "'% " + word + "%'";

         } else {
            sql += fieldName + like + "'% " + word + " %'";
         }

      } else {
         sql += fieldName + like + "'%" + word + "%'";
      }
   }

   if (sql !== "") {
      sql = "(" + sql + ")";
   }
   return sql;
}

//Build comma separated format (csv) text string.
export function build_csv(lay: Search.iLayer): string {
   const fs = lay.fs;
   let csv: string = "";

   //Write column heading names.
   let txt: string = "";
   let fields: Util_App.iField[] = Util_App.getFeatureValues(fs, fs.features[0], lay.name);

   for (const field of fields) {
      if (!lay.spec.doNotExport.Contains(field.name) && Settings.security.accessLevel >= field.accessLevel) {
         if (txt !== "") {
            txt += ",";
         }
         txt += field.alias;
      }
   }

   csv += txt + "\n";

   //Write row values.
   for (const feature of fs.features) {
      txt = "";
      fields = Util_App.getFeatureValues(fs, feature, lay.name);

      for (const field of fields) {
         if (!lay.spec.doNotExport.Contains(field.name) && Settings.security.accessLevel >= field.accessLevel) {
            let fieldValue: string = field.value.toString();
            fieldValue = fieldValue.replace(/"/g, " ").trim(); //Remove quotes.
            fieldValue = fieldValue.replace(/(\r\n|\n|\r)/gm, ""); //Remove line breaks.

            if (field.dataType !== "string" && field.dataType !== "date") {
               fieldValue = fieldValue.replace(/\$/g, "").replace(/,/g, "").trim();  //Remove formatting.
            }
            if (fieldValue.Contains(",")) {
               fieldValue = '"' + fieldValue + '"';  //Add quotes if comma in data value.
            }

            if (txt !== "") {
               txt += ",";
            }
            txt += fieldValue;
         }
      }
      csv += txt + "\n";
   }

   return csv;
}

//Build id list from featureSet.
export function buildIDList(fieldName: string, fs: __esri.FeatureSet): string[] {
   let id: string;
   const sa: string[] = [];

   const id_field: string = Util_App.getFieldNameCase(fieldName, fs.fields);
   if (!id_field) {
      Util.errMsg("Field name " + fieldName + " does not exist to build ID list.");
      return sa;
   }

   for (const feature of fs.features) {
      id = Util_App.getFieldValueFAT(id_field, feature);

      if (id === "") { //Don't add blank ids.
         id = Util_App.getObjectId(feature).toString();
         sa.Add(id);

      } else {
         sa.Add(id);
      }
   }
   return sa;
}

//Build where clause for id list.
export function buildIDListWhere(fieldName: string, isInteger: boolean, sa: string[] | number[]): string {
   let sql: string = "1=2";

   if (sa.length > 0) {
      sql = fieldName + " in (";

      if (isInteger) {
         sql += sa.join(",");
      } else {
         sql += "'" + sa.join("','") + "'";
      }
      sql += ")";
   }
   return sql;
}

//Build generic query clause.
export function buildQueryClause(fieldNameIn: string, operatorIn: string, valueIn: string, dataType: string = "string"): string {
   let fieldName: string = fieldNameIn;
   let operator: string = operatorIn;
   let value: string = valueIn;
   const noMatchValue: string = "~^~";
   let addNullCheck: boolean = false;
   let closeParen: string = ""; //Initialize close paren for "in" operator.
   let dateText: string = "";

   //Trim the input value for numbers and dates.
   if (dataType !== "string") {
      value = value.trim();
   }

   //Fix apostrophy syntax for plurals.
   value = value.replace(/'/g, "''");

   //Insert dashes on pin.
   if (Settings.insertDashesOnPIN) {
      value = Custom.addDashesToPIN(value);
   }

   //Fix bad data values for numbers and dates.
   if (dataType !== "string") {
      if (operatorIn.Contains("in (")) {
         let newValue: string = "";
         const sa: string[] = value.split(",");
         for (const txt of sa) {
            if (newValue !== "") {
               newValue += ",";
            }
            newValue += fixBadDataValue(operatorIn, txt, dataType);
         }
         value = newValue;

      } else {
         value = fixBadDataValue(operatorIn, value, dataType);
      }
   }

   //Create quote variable.
   let quote: string = "";
   if (dataType === "string" || dataType === "date") {
      quote = "'";
   }

   //Convert sq ft to acres.
   //if (((fieldName === "Area") || (fieldName === "Area_SqFt")) && (aliasName.toLowerCase().Contains("acres"))) {
   //   sql = (sql * 43560).toFixed(2);
   //}

   //User did not enter anything.
   if (operator === "=" || operator === "<>" || operator.Contains("in (")) {
      if (dataType === "string") {
         if (valueIn === "") {
            addNullCheck = true; //Add check for null values.
         }

      } else { //Numeric and date.
         if (valueIn.trim() === "") {
            addNullCheck = true; //Add check for null values.
         }
      }
   }

   //Build query clause.
   if (addNullCheck) {
      if (operator === "in (") {
         operator = "=";
      } else if (operator === "not in (") {
         operator = "<>";
      }

      //Process with and contains.
   } else if (operator.Contains("with") || operator.Contains("contain")) {
      if (operator.Contains("begin")) {
         if (value === "") {
            value = noMatchValue;
         } else {
            value += "%";
         }

      } else if (operator.Contains("end")) {
         if (value === "") {
            value = noMatchValue;
         } else {
            value = "%" + value;
         }

      } else if (operator.Contains("all the words")) { //Contains all words.
         if (operator.Contains("not")) {
            value = buildContainsAllWordsSQL(fieldName, "not LIKE", value);
         } else {
            value = buildContainsAllWordsSQL(fieldName, "LIKE", value);
         }

      } else if (operator.Contains("contain")) {
         if (value === "") {
            value = noMatchValue;
         } else {
            value = "%" + value + "%"; //Must be after contains all words.
         }
      }

      if (operator.Contains("all the words")) {
         fieldName = "";
         operator = "";
         quote = "";

         if (value === "") {
            fieldName = fieldNameIn;
            operator = "=";
            value = noMatchValue;
            quote = "'";
            if (operatorIn.Contains("not")) {
               operator = "<>";
            }
         }

      } else if (operator.Contains("not")) {
         operator = "not LIKE";
      } else {
         operator = "LIKE";
      }

      //Process "in" value list.
   } else if (operator.Contains("in (")) {
      if (dataType === "string" || dataType === "date") {
         let newValue: string = "','";
         if (dataType === "date") {
            newValue = "',date'"; //Add date syntax.
         }
         value = value.replace(/,/g, newValue);
      }
      closeParen = ")";
   }

   //Concatenate to form completed sql.
   const space: string = " ";
   if (dataType === "date") {
      dateText = "date"; //Set date syntax;
   }

   let sql: string = (fieldName + space + operator + space + dateText + quote + value + quote + closeParen).trim();

   if (addNullCheck) {
      if (dataType === "string") {
         sql = "(" + sql;
         if (operator === "=") {
            sql += " or " + fieldName + " is NULL)";
         } else {
            sql += " and " + fieldName + " is not NULL)";
         }

      } else {
         if (operator === "=") {
            sql = fieldName + " is NULL";
         } else {
            sql = fieldName + " is not NULL";
         }
      }
   }

   return sql;
}

//Clean-up user inputed data and split with space character.
export function cleanAndSplit(in_string: string, in_maxWords: number): string[] {
   let i: number;
   let txt: string;
   let sa: string[] = [];

   txt = in_string;
   txt = txt.replace(/\|/g, "");
   txt = txt.replace(/\[/g, "");
   txt = txt.replace(/\]/g, "");
   txt = txt.trim();

   for (i = 0; i < 4; i++) {
      txt = txt.replace(/\s\s\s\s\s/g, " ");
      txt = txt.replace(/\s\s\s\s/g, " ");
      txt = txt.replace(/\s\s\s/g, " ");
      txt = txt.replace(/\s\s/g, " ");
   }

   sa = txt.split(" ");

   for (i = sa.length - 1; i > in_maxWords; i--) {
      sa.pop();
   }

   if (sa.length === 1) {
      if (sa[0].trim() === "") {
         sa.Clear();
      }
   }
   return sa;
}

//DD to DM.
export function ddToDM(dd: number, addZero: boolean): string {
   const d: number = Util.parseInteger(dd.toString());
   const dF: number = Math.abs(dd - d);
   let m: number = dF * 60;
   const u: number = 1000;
   m = Util.parseInteger((m * u + (5 / 10)).toString()) / u;

   let deg: string = d.toString();
   let minute: string = m.toString();

   if (addZero) {
      if (d < 10) {
         deg = "0" + d;
      }
      if (m < 10) {
         minute = "0" + m;
      }
   }
   const dm: string = (deg + '\xB0 ' + minute + '\'');
   return dm;
}

//DD to DMS.
export function ddToDMS(dd: number, addZero: boolean, addDecimal: boolean): string {
   const d: number = Util.parseInteger(dd.toString());
   const dF: number = Math.abs(dd - d);
   const m: number = Util.parseInteger((dF * 60).toString());
   let s: number = dF * 3600 - m * 60;
   let deg: string = d.toString();
   let minute: string = m.toString();

   if (addDecimal) {
      const u: number = 10;
      s = Util.parseInteger((s * u + (5 / 10)).toString()) / u;
   } else {
      s = Math.round(s);
   }
   let sec: string = s.toString();

   if (addZero) {
      if (d < 10) {
         deg = "0" + d;
      }
      if (m < 10) {
         minute = "0" + m;
      }
      if (s < 10) {
         sec = "0" + s;
      }
   }
   const dms: string = deg + '\xB0 ' + minute + '\' ' + sec + '"';
   return dms;
}

//Convert distance in feet to formatted text.
export function distanceToText(distance: number): string {
   const distFeet: number = Util.round(distance, 1);
   const distMile: number = Util.round((distance / 5280), 2);

   let distText: string = distFeet + " ft";
   if (distMile >= 0.25) {
      distText = distMile + " mi";
   }
   return distText;
}

//Convert feet to meters.
export function feetToMeters(distFeet: number): number {
   let distMercator = distFeet;
   if (Util_App.isWebMercator()) {
      distMercator = distFeet * 0.3048;
   }
   return distMercator;
}

//Fix bad data value.
export function fixBadDataValue(operator: string, valueIn: string, dataType: string): string {
   let value: string = valueIn;

   //Date data type.
   if (dataType === "date") {
      if (!value.IsValidDate()) {
         if (operator === "<" || operator === "<=") {
            value = "1/1/1769";
         } else {
            value = "1/1/2099";
         }
      }

      //Numeric data type.
   } else if (dataType !== "string") {
      if (!value.IsNumeric()) {
         if (dataType === "small-integer") {
            value = "32768";
         } else {
            value = "2147000000";
         }
         if (operator === "<" || operator === "<=") {
            value = "-" + value;
         }

      } else {
         value = Util.parseFlt(value).toString();
      }
   }

   return value;
}

//Compute angle between two points.  Counter-clockwise from east by default.
export function getAngle(pnt1: __esri.Point, pnt2: __esri.Point, northClockwise?: boolean): number {
   const xD: number = pnt2.x - pnt1.x;
   const yD: number = pnt2.y - pnt1.y;

   if ((Math.abs(xD) < 1) && (Math.abs(yD) < 1)) {
      return 0;
   }
   const rad: number = Math.atan2(yD, xD);
   let deg: number = rad * (180 / Math.PI);

   if (deg < 0) {
      deg += 360;
   }

   if (northClockwise) {
      deg = 360 - (deg - 90);
      if (deg < 0) {
         deg += 360;
      }
      if (deg > 360) {
         deg -= 360;
      }
   }
   return deg;
}

//Compute bearing.
export function getBearing(pnt1: __esri.Point, pnt2: __esri.Point) {
   let beg: string;
   let end: string;
   let bear: number;

   const xD: number = pnt1.x - pnt2.x;
   const yD: number = pnt1.y - pnt2.y;

   if ((Math.abs(xD) < 1) && (Math.abs(yD) < 1)) {
      return "";
   }
   const rad: number = Math.atan2(yD, xD);
   const deg: number = rad * (180 / Math.PI) + 90;
   end = "E";

   if (deg > -90 && deg <= 90) {
      beg = "N";
      if (deg > 0) {
         end = "W";
      }
      bear = Math.abs(deg);

   } else {
      beg = "S";
      if (deg < 180) {
         end = "W";
      }
      bear = Math.abs(180 - deg);
   }
   const dms: string = Util_App.ddToDMS(bear, true, false);
   return beg + dms + end;
}

//Get clipped overlay features.  For polys, order click point poly first. "acres" or "square-feet"
export function getClippedOverlayFeatures(features: __esri.Graphic[], clipPoly: __esri.Polygon, areaUnit?: __esri.AreaUnits, clickPnt?: __esri.Point, orderClickFirst?: boolean): iOverlayfeatures[] {
   const outFeatures: iOverlayfeatures[] = [];
   if (!clipPoly || features.length === 0) {
      return outFeatures;
   }
   interface iOrderBy {
      feature: __esri.Graphic;
      orderBy: number;
      clickedIn: boolean;
   }
   const orderFeatures: iOrderBy[] = [];
   let orderFeature: iOrderBy;
   let area: number;
   let newFeature: __esri.Graphic;

   for (const feature of features) {
      const geom_intersect = geometryEngine.intersect(feature.geometry, clipPoly);

      let geom = geom_intersect as __esri.Geometry;
      if (geom && geom.constructor === Array) {
         geom = geom[0];
      }

      if (geom) {
         newFeature = feature.clone();
         newFeature.geometry = geom.clone();

         if (feature.geometry.type === "polygon") {
            const poly = geom as __esri.Polygon;

            if (MainMap.activeView.spatialReference.isWebMercator) {
               area = geometryEngine.geodesicArea(poly, areaUnit);
            } else {
               area = geometryEngine.planarArea(poly, areaUnit);
            }

            let clickedIn: boolean = false;
            if (clickPnt) {
               clickedIn = geometryEngine.within(clickPnt, geom);
            }
            orderFeature = { feature: newFeature, orderBy: area, clickedIn: clickedIn };

         } else { //Points and lines.
            let nearPnt = { distance: 0 };
            if (clickPnt) {
               nearPnt = geometryEngine.nearestVertex(geom, clickPnt);
            }

            let distance: number = nearPnt.distance;
            if (MainMap.activeView.spatialReference.isWebMercator) {
               distance = metersToFeet(distance) * Settings.mercatorDistanceFudgeFactor;
            }
            orderFeature = { feature: newFeature, orderBy: distance, clickedIn: false };
         }

         orderFeatures.Add(orderFeature);
      }
   }

   //Sort by area or distance from click point.
   if (features[0].geometry.type === "polygon") {
      orderFeatures.sort(Util.sortArray_reverse("orderBy"));
   } else {
      orderFeatures.sort(Util.sortArray("orderBy"));
   }

   //Build output feature array.
   if (orderClickFirst) {
      for (const rec of orderFeatures) {
         if (rec.clickedIn) {
            outFeatures.Add({ feature: rec.feature, areaOrDistance: rec.orderBy });
            break;
         }
      }
   }

   for (const rec of orderFeatures) {
      if (orderClickFirst) {
         if (!rec.clickedIn) {
            outFeatures.Add({ feature: rec.feature, areaOrDistance: rec.orderBy });
         }

      } else {
         outFeatures.Add({ feature: rec.feature, areaOrDistance: rec.orderBy });
      }
   }

   return outFeatures;
}

//Get popup data table spec by title.
export function getDataTableSpec(title: string): Settings.iPopUpTable {
   for (const popupTable of Settings.popupTables) {
      if (popupTable.title.toLowerCase() === title.toLowerCase()) {
         const popupTableSpec = JSON.parse(JSON.stringify(popupTable)) as Settings.iPopUpTable; //Clone it.
         return popupTableSpec;
      }
   }
   return null;
}

//Get feature fields, values, alias names, etc.
export function getFeatureValues(fs: __esri.FeatureSet, feature: __esri.Graphic, layerName: string, fieldNameMatch?: string, linkFieldName?: string): iField[] {
   let fieldName: string;
   let fieldValue: any;
   let formatType: string;
   let url: string;
   let dataType: string;
   let custom: any;
   let alias: iAlias;
   const fields: iField[] = [];

   //Create alias names for display.
   for (const fsField of fs.fields) {
      fieldName = fsField.name;

      //Only return one field name if specified in input argument.
      if (!fieldNameMatch || (fieldNameMatch && (fieldName.toLowerCase() === fieldNameMatch.toLowerCase()))) {
         alias = Util_App.parseAliasName(fsField.alias);
         dataType = fsField.type;
         formatType = alias.formatType;
         fieldValue = Util_App.getFieldValueFAT(fieldName, feature, layerName);
         url = "";

         if (dataType === "date") {
            fieldValue = new Date(fieldValue);
            if (formatType === "") {
               formatType = "ShortDate";
            }

         } else if (dataType === "oid" || dataType === "global-id" || fieldName === "Shape") {
            alias.accessLevel = 999;
         }

         //Set field value.
         fieldValue = Util.formatTextValue(fieldValue, formatType);

         //Set link url from calling argument.
         if (linkFieldName) {
            url = getFieldValueNoCase(linkFieldName, feature, fs);
         }

         //Get custom values and link urls.
         custom = Custom.getCustomValueFeature(fieldName, fieldValue, feature, layerName, fs);
         fieldValue = custom.value;
         if (custom.url !== "") {
            url = custom.url;
         }

         fields.Add({ name: fieldName, alias: alias.name, value: fieldValue, url: url, formatType: formatType, dataType: dataType, accessLevel: alias.accessLevel });
      }
   }

   return fields;
}

//Get field index by field name and featureset.
export function getFieldIndexByName(fieldName: string, fs: __esri.FeatureSet) {
   let i: number;
   let name: string;
   if (fs.fields && fs.fields.length > 0) {
      for (i = 0; i < fs.fields.length; i++) {
         name = fs.fields[i].name.toLowerCase();
         if (name === fieldName.toLowerCase()) {
            return i;
         }
      }
   }
   return -1;
}

//Get case sensitive ESRI attribute field name.
export function getFieldNameCase(fieldName: string, fieldsOrAttrs: any, ignoreAccessLevel: boolean = false): string {
   if (fieldsOrAttrs) {
      let propName: string;
      let alias: iAlias;
      const fields: string[] = [];

      //Build array of field names.
      if (Array.isArray(fieldsOrAttrs)) {
         for (const fieldsOrAttr of fieldsOrAttrs) {
            fields.Add(fieldsOrAttr.name);
         }

      } else {
         for (propName in fieldsOrAttrs) {
            if (Object.prototype.hasOwnProperty.call(fieldsOrAttrs, propName)) {
               alias = Util_App.parseAliasName(propName);
               if (ignoreAccessLevel || Settings.security.accessLevel >= alias.accessLevel) {
                  fields.Add(alias.name);
               }
            }
         }
      }

      for (const field of fields) {
         if (field.toLowerCase() === fieldName.toLowerCase()) {
            return field;
         }
      }

   }
   return null;
}

//Get feature attribute value. Check for null database values and coded value domains.
export function getFieldValueFAT(fieldName: string, feature: __esri.Graphic, layerName?: string, ignoreAccessLevel: boolean = false): any {
   if (feature) {
      if (fieldName) {
         let fieldValue: any;
         let propName: string;
         let alias: iAlias;

         for (propName in feature.attributes) {
            if (Object.prototype.hasOwnProperty.call(feature.attributes, propName)) {
               alias = Util_App.parseAliasName(propName);

               if (ignoreAccessLevel || Settings.security.accessLevel >= alias.accessLevel) {

                  if (alias.name.toLowerCase() === fieldName.toLowerCase()) {
                     fieldValue = feature.attributes[propName];
                     if ((typeof fieldValue === "undefined") || (fieldValue === null) || (fieldValue === "Null")) {
                        fieldValue = "";
                     }

                     //Get coded value.
                     if (layerName) {
                        for (const codedValue of Settings.codedValues) {
                           if (layerName === codedValue.layerName && fieldName === codedValue.fieldName) {
                              for (const value of codedValue.values) {
                                 if (fieldValue === value.code) {
                                    fieldValue = value.name;
                                    break;
                                 }
                              }
                           }
                        }
                     }

                     return fieldValue;
                  }

               } //End check access level.
            } //End has own property.
         } //End loop for all feature attribute properties.

      } //End fieldname argument was specified.
   } //End feature argument was specified.
   return "";
}

//Get feature attribute value not case sensitive.
export function getFieldValueNoCase(fieldName: string, feature: __esri.Graphic, fs: __esri.FeatureSet): any {
   let fn: string;
   let fieldValue: any = "";

   if (fieldName && feature && fs) {

      for (const field of fs.fields) {
         fn = field.name;
         if (fn.toLocaleLowerCase() === fieldName.toLocaleLowerCase()) {
            fieldValue = feature.attributes[fn];
            if (typeof fieldValue === "undefined") {
               fieldValue = "";
            }
            if (fieldValue === null) {
               fieldValue = "";
            }
            break;
         }
      }
   }
   return fieldValue;
}

//Get heading fields and values.
export function getHeadingValues(fs: __esri.FeatureSet, feature: __esri.Graphic, layerName: string): iField[] {
   let fieldName: string;
   let headerFields: Util_App.iField[];
   const fields: iField[] = [];
   const layerSpec: Settings.iLayer = Util_App.getLayerSpec(layerName);
   const sfs: string[] = layerSpec.headingFields;

   if (sfs) {
      //Process all heading field names.
      for (const sf of sfs) {
         fieldName = Util_App.getFieldNameCase(sf, fs.fields);
         if (fieldName) {
            headerFields = Util_App.getFeatureValues(fs, feature, layerName, fieldName);
            fields.Add(headerFields[0]);
         }
      }
   }

   //Add the first field if none specified in settings.
   if (fields.length === 0) {
      headerFields = Util_App.getFeatureValues(fs, feature, layerName, fs.fields[0].name);
      fields.Add(headerFields[0]);
   }
   return fields;
}

//Get highlight symbology.
export function getHighlightSymbol(fs: __esri.FeatureSet, toolName?: string): any {
   let sym = null;

   if (toolName === "Identify") {
      if (fs.geometryType === "point") {
         sym = Settings.identifyHighlightPointSym;

      } else if (fs.geometryType === "polyline") {
         sym = Settings.identifyHighlightLineSym;

      } else { //polygon.
         sym = Settings.identifyHighlightPolygonSym;
      }

   } else { //Search
      if (fs.geometryType === "point") {
         sym = Settings.selectedHighlightPointSym;

      } else if (fs.geometryType === "polyline") {
         sym = Settings.selectedHighlightLineSym;

      } else { //polygon.
         sym = Settings.selectedHighlightPolygonSym;
      }
   }
   return sym;
}

//Get all checked-on sub layer indexes.
export function getHoverSubLayerIndexes(layer: __esri.MapImageLayer): number[] {
   Util.logMsg("Util_App.getIdentifiableLayerIndexes");
   const layerIndexes: number[] = [];

   //Process all layer settings.
   for (const idSpec of Settings.identifyLayersMI) {
      if (idSpec.mapServiceID === layer.id) {

         if (layer.allSublayers) {

            for (const sublayerName of idSpec.layerName) {
               const sublayer = getSublayer(sublayerName, layer.allSublayers);
               if (sublayer) {

                  //Process a single selectable sublayer.
                  if (SelectOnMap.active && SelectOnMap.selectableLayer !== "All Visible Layers") {
                     const subName: string = sublayer.title.replace(" Highlight", "");
                     if (subName === SelectOnMap.selectableLayer) {
                        layerIndexes.Add(sublayer.id);
                     }

                  } else { //Process all sublayers.
                     if (isSublayerVisible(idSpec.mapServiceID, sublayer.title, false)) {
                        layerIndexes.Add(sublayer.id);
                     }
                  }
               }
            }
         }
      }
   }
   return layerIndexes;
}

//Get is integer simple search for field name.
export function getIsIntegerByFieldName(layerName: string, fieldName: string): boolean {
   const laySpec: Settings.iLayer = Util_App.getLayerSpec(layerName);
   let isInteger: boolean = false;

   const i = laySpec.simpleSearchFields.indexOf(fieldName);
   if (i !== -1) {
      isInteger = laySpec.simpleSearchIsInteger[i];
   }
   return isInteger;
}

//Get layer and field name from layer-alias string.
export function getLayerAndFieldFromLayerDashAlias(layerDashAlias: string): iLayerField {
   const sa: string[] = layerDashAlias.split("-");
   const layerName: string = sa[0].trim();
   const aliasName: string = sa[1].trim();
   let fieldName: string = "Not found";
   const laySpec: Settings.iLayer = getLayerSpec(layerName);

   if (laySpec) {
      const i: number = laySpec.simpleSearchAliases.indexOf(aliasName);
      if (i !== -1) {
         fieldName = laySpec.simpleSearchFields[i];
      }
   }

   return { layerName: layerName, fieldName: fieldName };
}

//Get layer and field name from layer-field string.
export function getLayerAndFieldFromLayerDashField(layerDashField: string): iLayerField {
   const sa: string[] = layerDashField.split("-");
   const layerName: string = sa[0].trim();
   let fieldName: string = "";
   if (sa.length === 2) {
      fieldName = sa[1].trim();
   }
   return { layerName: layerName, fieldName: fieldName };
}

//Get layer - alias from layer-field string.
export function getLayerDashAliasFromLayerDashField(layerDashField: string): string {
   const layerAndField: Util_App.iLayerField = Util_App.getLayerAndFieldFromLayerDashField(layerDashField);
   const layerName: string = layerAndField.layerName;
   const fieldName: string = layerAndField.fieldName;
   const laySpec: Settings.iLayer = Util_App.getLayerSpec(layerName);
   let aliasName: string = "Not found";

   if (laySpec) {
      const i = laySpec.simpleSearchFields.IndexOfNoCase(fieldName);
      if (i !== -1) {
         aliasName = laySpec.simpleSearchAliases[i];
      }
   }
   const layerDashAlias = layerName + " - " + aliasName;
   return layerDashAlias;
}

//Form full objectid path by display field name.
export function getLayerIDField(laySpec: Settings.iLayer): string {
   let id_field: string = "";

   if (laySpec) {
      id_field = laySpec.id_FieldName;
   }
   return id_field;
}

//Get display layers spec by layer name with optional map service name.
export function getLayerSpec(layerName: string, msName: string = null): Settings.iLayer {
   let outLay: Settings.iLayer = null;

   for (const lay of Settings.layers) {
      if (lay.layerName.toLowerCase() === layerName.toLowerCase()) {
         if (msName) {
            if (lay.mapServiceID.toLowerCase() === msName.toLowerCase()) {
               outLay = lay;
               break;
            }
         } else {
            outLay = lay;
            break;
         }
      }
   }
   return outLay;
}

//Get query full path to url from layer spec.
export function getLayerURL(layerSpec: Settings.iLayer): string {
   let url: string = layerSpec.mapServiceURL;
   if (!url.Contains("FeatureServer")) {
      url += "/" + layerSpec.layerIndex;
   }
   return url;
}

//Get map layer by id including unloaded basemap layers. (not sublayer)
export function getLayerById(layerId: string, includeUnloadedBasemaps?: boolean): any {
   //Get loaded map service or loaded basemap.
   for (let i: number = 0; i < MainMap.map.allLayers.length; i++) {
      const layer = MainMap.map.allLayers.getItemAt(i);
      if (layer.id === layerId) {
         return (layer);
      }
   }

   //Get unloaded basemap.
   if (includeUnloadedBasemaps) {
      for (const basemap of MainMap.basemaps) {
         if (basemap.id === layerId) {
            return (basemap.baseLayers.getItemAt(0));
         }
      }
   }
   return null;
}

//Get layer list spec by layer name.
export function getLayerListSpec(layerName: string): Settings.iLayerList {
   for (const ll of Settings.layerLists) {
      if (ll.layerName.toLowerCase() === layerName.toLowerCase()) {
         return ll;
      }
   }
   return null;
}

//Get operational map layer by id. (not sublayer)
export function getOperationalLayerById(layerId: string): any {
   const mapLayer = MainMap.map.findLayerById(layerId);

   if (mapLayer && (mapLayer.id !== MainMap.map.basemap.id)) {
      return mapLayer;
   }
   return null;
}

//Get mbr for featureset.
export function getMBRFeatureSet(fs: __esri.FeatureSet): __esri.Extent {
   let mbr: __esri.Extent = null;

   let xMin: number = 99999999;
   let yMin: number = 99999999;
   let xMax: number = -99999999;
   let yMax: number = -99999999;

   for (const ftr of fs.features) {
      if (ftr.geometry) {

         if (ftr.geometry.type === "point") {
            const point = ftr.geometry as __esri.Point;

            if (point.x === 0) {
               point.x = MainMap.mapExtentFull.xmin;
            }
            if (point.y === 0) {
               point.y = MainMap.mapExtentFull.ymin;
            }

            mbr = new Extent({ xmin: point.x, ymin: point.y, xmax: point.x, ymax: point.y, spatialReference: MainMap.activeView.spatialReference });

         } else {
            mbr = ftr.geometry.extent;
         }
         if (mbr) {
            if (!isNaN(mbr.xmin)) {
               xMin = Math.min(xMin, mbr.xmin);
               yMin = Math.min(yMin, mbr.ymin);
               xMax = Math.max(xMax, mbr.xmax);
               yMax = Math.max(yMax, mbr.ymax);
            }
         }
      }
   }
   if (xMin !== 99999999) {
      mbr = new Extent({ xmin: xMin, ymin: yMin, xmax: xMax, ymax: yMax, spatialReference: MainMap.activeView.spatialReference });
   }
   return mbr;
}

//Find middle point of a line.
export function getMidPoint(pnt1: __esri.Point, pnt2: __esri.Point): __esri.Point {
   const midPoint = new Point({ x: (pnt1.x + pnt2.x) / 2, y: (pnt1.y + pnt2.y) / 2, spatialReference: MainMap.activeView.spatialReference });
   return midPoint;
}

//Get the nearest features to a point.
export function getNearestFeatures(fs: __esri.FeatureSet, pnt: __esri.Point, maxFeatures?: number): Util_App.iNearFeatures[] {
   const nearFeatures: Util_App.iNearFeatures[] = [];

   for (const feature of fs.features) {
      const nearPnt: __esri.NearestPointResult = geometryEngine.nearestVertex(feature.geometry, pnt);

      let distance: number = nearPnt.distance;
      if (MainMap.activeView.spatialReference.isWebMercator) {
         distance = Util_App.metersToFeet(distance) * Settings.mercatorDistanceFudgeFactor;
      }
      nearFeatures.Add({ distance: distance, feature: feature });
   }

   //Order by distance.
   nearFeatures.sort(Util.sortArray("distance"));

   //Cull the herd a bit.
   if (maxFeatures) {
      if (nearFeatures.length > maxFeatures) {
         nearFeatures.length = maxFeatures;
      }
   }
   return nearFeatures;
}

//Get object ID value.
export function getObjectId(feature: __esri.Graphic): number {
   const objectId = feature.getObjectId();
   if (objectId) {
      return objectId;
   } else {
      return -1;
   }
}

//Get 1 to many spec by field name.
export function getOneToManySpec(fieldName: string, layerSpec: Settings.iLayer): string[] {
   if (layerSpec && layerSpec.oneToMany) {
      for (const spec of layerSpec.oneToMany) {
         if (spec.StartsWith(fieldName + ",")) {
            return spec.split(",");
         }
      }
   }
   return null;
}

//Get point inside of a polygon.
export function getPointInsidePolygon(poly: __esri.Polygon, quick: boolean = false): __esri.Point {
   let pnt: __esri.Point;
   let inside: boolean;

   //Get visual centroid if not using the quick method.
   if (!quick) {
      pnt = getVisualCentroid(poly);
      inside = geometryEngine.within(pnt, poly);
      if (inside) {
         return pnt;
      }
   }

   //Try extent center.
   pnt = poly.extent.center;
   inside = geometryEngine.within(pnt, poly);
   if (inside) {
      return pnt;
   }

   //Try centroid property of polygon.
   pnt = poly.centroid;
   inside = geometryEngine.within(pnt, poly);
   if (inside) {
      return pnt;
   }

   //Add intermediate points to the poly.
   let densePoly: __esri.Geometry;
   if (MainMap.activeView.spatialReference.isWebMercator) {
      densePoly = geometryEngine.geodesicDensify(poly, 25, "feet");
   } else {
      densePoly = geometryEngine.densify(poly, 25, "feet");
   }

   //Find closest vertex.
   let nearVertex: __esri.NearestPointResult = geometryEngine.nearestVertex(densePoly, poly.extent.center);

   //Search circular points around the closest vertex.
   if (nearVertex) {
      let nearPnt = nearVertex.coordinate;

      for (let i: number = 1; i < 359; i += 10) {
         const degree: number = i * (Math.PI / 180);
         const xOffset: number = Math.cos(degree) * 0.2;
         const yOffset: number = Math.sin(degree) * 0.2;
         pnt = new Point({ x: nearPnt.x + xOffset, y: nearPnt.y + yOffset, spatialReference: MainMap.activeView.spatialReference });

         inside = geometryEngine.within(pnt, poly);
         if (inside) {
            return pnt;
         }
      }
      return nearPnt;
   }
   return pnt;
}

//Get select by point tolerance.
export function getSelectByPointTolerance(asDistance: boolean = false): number {
   let tol: number = Util.parseInteger((11000 / MainMap.activeView.scale).toString()); //Was 11000
   tol = Math.max(6, tol); //Was 6
   tol = Math.min(32, tol);
   if (asDistance) {
      tol *= MainMap.activeView.resolution; //Convert pixels to distance in map units.

      if (MainMap.activeView.scale > 200000) {
         tol = tol / 3;
      } else if (MainMap.activeView.scale > 100000) {
         tol = tol / 2;
      } else if (MainMap.activeView.scale > 50000) {
         tol = tol / 1.5;
      }
   }
   //Util.errMsg("Scale: " + MainMap.activeView.scale + "  Tol by Scale: " + tol);
   return tol;
}

//Get quick search alias name by field name.
export function getSimpleSearchAliasByFieldName(layerName: string, fieldName: string): string {
   const laySpec: Settings.iLayer = getLayerSpec(layerName);
   let aliasName: string = "Not found";

   if (laySpec) {
      const i: number = laySpec.simpleSearchFields.IndexOfNoCase(fieldName);
      if (i !== -1) {
         aliasName = laySpec.simpleSearchAliases[i];
      }
   }
   return aliasName;
}

//Get sublayer. Finds the first non-folder match in a given map service.
export function getSublayer(subLayerName: string, sublayers: __esri.Collection<__esri.Sublayer>): __esri.Sublayer {
   for (const sublayer of sublayers) {
      if ((sublayer.title.toLowerCase() === subLayerName.toLowerCase()) && !sublayer.sublayers) {
         return sublayer;
      }
   }
   return null;
}

//Get all checked-on and visible layer names.
export function getVisibleSelectOnMapLayerNames(selectOrIdentify: string): GetSearchResults.iSelectableLayers[] {
   const layerNames: GetSearchResults.iSelectableLayers[] = [];

   //Process all layer settings.
   for (const laySpec of Settings.layers) {

      if ((selectOrIdentify === "Select" && laySpec.selectFrom) ||
         (selectOrIdentify === "Identify" && laySpec.identifyFrom) ||
         (selectOrIdentify === "All" && (laySpec.selectFrom || laySpec.identifyFrom))) {
         const layer = Util_App.getOperationalLayerById(laySpec.mapServiceID) as __esri.MapImageLayer;

         if (!laySpec.layerName.EndsWith(" Highlight") && layer && layer.visible) {

            //Process map image sublayers.
            if (layer.allSublayers && layer.allSublayers.length > 0) {
               const sublayer = getSublayer(laySpec.layerName, layer.allSublayers);
               if (sublayer) {
                  if (isSublayerVisible(laySpec.mapServiceID, sublayer.title)) {
                     layerNames.Add({ name: laySpec.layerName, fields: [] });
                  }
               }

            } else { //Process feature layers.
               if (Util_App.isMapLayerVisible(layer.id)) {
                  layerNames.Add({ name: laySpec.layerName, fields: [] });
               }

            }
         }
      }
   }
   return layerNames;
}

//Get visual centoid of polygon.
export function getVisualCentroid(polyGeom: __esri.Polygon): __esri.Point {
   const pnt = new Point({ x: 1, y: 1, spatialReference: MainMap.activeView.spatialReference });

   //Check for corrupt input polygon.
   if (!polyGeom) {
      return pnt;
   } else if (!polyGeom.rings) {
      if (polyGeom.extent && polyGeom.extent.center) {
         return polyGeom.extent.center;
      } else {
         return pnt;
      }
   }

   //Generalize the polygon.
   let tol: number = 6;
   let extentArea = polyGeom.extent.width * polyGeom.extent.height;
   if (MainMap.activeView.spatialReference.isWebMercator) {
      extentArea = Util_App.metersToFeet(extentArea);
   }

   if (extentArea > 6000000) {
      tol = extentArea / 1000000;
   }
   let poly = geometryEngine.generalize(polyGeom, tol, false, "feet") as __esri.Polygon;

   //Show results.
   //const gra = new Graphic({ geometry: poly, symbol: Settings.testPolygonSym });
   //GraLay.temp.add(gra);

   //Perform negative buffer.
   let i: number = 0;
   let bufGeom: __esri.Polygon | __esri.Polygon[] = null;

   let tryDist = poly.extent.width;
   if (poly.extent.height < poly.extent.width) {
      tryDist = poly.extent.height;
   }
   tryDist *= 0.15;

   while (i < 5 && tryDist > 5) {
      if (MainMap.activeView.spatialReference.isWebMercator) {
         bufGeom = geometryEngine.geodesicBuffer(poly, -tryDist, "feet");
      } else {
         bufGeom = geometryEngine.buffer(poly, -tryDist, "feet");
      }

      if (bufGeom && bufGeom.constructor !== Array) {
         const tempPoly = bufGeom as __esri.Polygon;
         poly = tempPoly.clone();
      } else {
         tryDist *= 0.30;
      }
      i++;
   }

   //Delete all but largest polygon ring.
   if (poly.rings.length > 1) {
      let j: number;
      let maxPnt: number = 0;

      for (i = 0; i < poly.rings.length; i++) {
         const numPnt: number = poly.rings[i].length;
         if (numPnt > maxPnt) {
            maxPnt = numPnt;
            j = i; //Save index to keeper ringer.
         }
      }

      for (i = poly.rings.length - 1; i >= 0; i--) {
         if (i !== j) {
            poly.removeRing(i);
         }
      }
   }

   //Show results. Comment out.
   //const gra2 = new Graphic({ geometry: poly, symbol: Settings.testPolygonSym2 });
   //GraLay.temp.add(gra2);

   //Use resulting centroid.
   const centroid = poly.extent.center;
   return centroid;
}

//Highlight a graphic element.
export function highlightGraphic(graphic: __esri.Graphic, genericHover?: boolean) {
   if (graphic) {
      const gra = graphic.clone();
      gra.attributes = null;
      gra.popupTemplate = null;

      if (genericHover) { //Generic hover for select and identify tools.
         if (graphic.geometry.type === "point") {
            gra.symbol = Settings.hoverPointSym;
            GraLay.hoverPoint.add(gra);

         } else if (graphic.geometry.type === "polyline") {
            gra.symbol = Settings.hoverLineSym;
            GraLay.hoverLine.add(gra);

         } else { //polygon.
            gra.symbol = Settings.hoverPolygonSym;
            GraLay.hoverPoly.add(gra);
         }

      } else { //Hover over selected feature.
         if (graphic.geometry.type === "point") {
            gra.symbol = Settings.selectedHoverPointSym;
            GraLay.selectedHoverPoint.add(gra);

         } else if (graphic.geometry.type === "polyline") {
            gra.symbol = Settings.selectedhoverLineSym;
            GraLay.selectedHoverLine.add(gra);

         } else { //polygon.
            gra.symbol = Settings.selectedhoverPolygonSym;
            GraLay.selectedHoverPoly.add(gra);
         }
      }
   }
}

//Check if layer or sublayer is visible.
export function isMapLayerVisible(mapServiceId: string, ignoreMapScale: boolean = false): boolean {
   const layer = Util_App.getOperationalLayerById(mapServiceId) as __esri.MapImageLayer;
   if (!layer || (!layer.visible)) {
      return false;
   }

   //Check group layers.
   for (let i: number = 0; i < MainMap.map.allLayers.length; i++) {
      const groupLayer = MainMap.map.allLayers.getItemAt(i) as __esri.GroupLayer;

      if (!groupLayer.visible && groupLayer.type === "group") {
         for (let j: number = 0; j < groupLayer.layers.length; j++) {
            const layer2 = groupLayer.layers.getItemAt(j);
            if (layer2.id === layer.id) {
               return false;
            }
         }
      }
   }

   //Check map scale.
   if (ignoreMapScale) {
      return true;
   }
   const mapScale: number = MainMap.activeView.scale;

   if (mapScale >= layer.maxScale) {
      if (mapScale <= layer.minScale || layer.minScale === 0) {
         return true;
      }
   }
   return false;
}

//Check if parent sublayer is visible recursively.
function isParentSublayerVisible(sublayer: __esri.Sublayer): boolean {
   if (sublayer.parent) {
      if (sublayer.parent.visible) {
         isParentSublayerVisible(sublayer.parent as __esri.Sublayer);
      } else {
         return false;
      }
   }
   return true;
}

//Set is web mercator flag.
export function isWebMercator(): boolean {
   if (MainMap.activeView) {
      return (MainMap.activeView.spatialReference.isWebMercator);

   } else {
      if (Settings.wkID === 102113 || Settings.wkID === 102100 || Settings.wkID === 3857) {
         return (true);
      }
   }
   return false;
}

//Check if sublayer is visible for map image service.
export function isSublayerVisible(mapServiceId: string, subLayerName: string, ignoreMapScale: boolean = false): boolean {
   if (!Util_App.isMapLayerVisible(mapServiceId, ignoreMapScale)) {
      return false;
   }

   //Process all sub layers.
   const layer = Util_App.getOperationalLayerById(mapServiceId) as __esri.MapImageLayer;

   if (layer.allSublayers) {
      const mapScale: number = MainMap.activeView.scale;

      for (const sublayer of layer.allSublayers) {
         if (!sublayer.sublayers && (sublayer.visible || sublayer.title.EndsWith(" Highlight"))) {
            if (sublayer.title.toLowerCase() === subLayerName.toLowerCase()) {
               if (isParentSublayerVisible(sublayer)) {
                  if (ignoreMapScale) {
                     return true;
                  }
                  if (mapScale >= sublayer.maxScale) {
                     if (mapScale <= sublayer.minScale || sublayer.minScale === 0) {
                        //Util.errMsg("Visible: " + sublayer.title);
                        return true;
                     }
                  }
               }
            }
         }
      }
      return false;
   }
   return false;
}

//QC for parenthesis mismatch.
export function isValidQueryParenthesis(str: string): boolean {
   let i: number = 0;

   for (const c of str) {
      if (c === "(") {
         i++;

      } else if (c === ")") {
         i--;
         if (i < 0) {
            return false;
         }
      }
   }

   if (i !== 0) {
      return false;
   }
   return true;
}

//Convert mercator meters to feet.
export function metersToFeet(distMercator: number): number {
   let distFeet = distMercator;

   if (Util_App.isWebMercator()) {
      distFeet = distMercator * 3.28084;
   }
   return distFeet;
}

//Open the print window.
export function openPrintWindow(title: string, windowName: string = "") {
   const newWin = window.open("", windowName);
   if (newWin) {
      const html: string = document.getElementById("print_div").innerHTML;
      newWin.document.title = title;
      newWin.document.body.innerHTML = html;
      newWin.focus();
   }
}

//Strip format type and access level from alias string.
export function parseAliasName(aliasName: string): iAlias {
   //let alias = {} as iAlias; //Weird syntax.
   const alias: iAlias = { name: aliasName, formatType: "", accessLevel: 0 };

   //Strip format type.
   let pos: number = aliasName.indexOf("(");
   if (pos !== -1) {
      if (!aliasName.EndsWith("()")) {
         alias.formatType = aliasName.SubStr(pos).replace("(", "").replace(")", "").trim();
         alias.name = aliasName.SubStr(0, pos).trim();
      }
   }

   //Strip access level.
   pos = alias.name.indexOf("!");
   if (pos !== -1) {
      alias.accessLevel = Util.parseInteger(alias.name);
      alias.name = alias.name.SubStr(pos + 1).trim();
   }
   return alias;
}

//Set active layer.
export function setActiveLayer(layerName: string) {
   if (layerName.trim() !== "") {

      //Turn on the map service and active layer.
      if (!layerName.trim().Equal(Search.activeLayer)) {
         const layerSpec = Util_App.getLayerSpec(layerName);
         if (layerSpec) {
            const layer = Util_App.getOperationalLayerById(layerSpec.mapServiceID) as __esri.MapImageLayer;
            if (layer) {
               layer.visible = true;
               if (layer.parent) {
                  const parentLayer = layer.parent as __esri.GroupLayer;
                  parentLayer.visible = true;
               }
            }

            if (layer.sublayers) {
               const sublayer = Util_App.getSublayer(layerName, layer.allSublayers);
               if (sublayer) {
                  setSublayerVisible(layerName, layer.allSublayers);
               }
            }
         }
      }

      //Set active layer.
      Search.setActiveLayerName(layerName.trim());

      //Set active search layer index.
      Search.setActiveLayerIndex(0);

      for (let i = 0; i < Search.layers.length; i++) {
         if (Search.layers[i].name === layerName) {
            Search.setActiveLayerIndex(i);
            break;
         }
      }
   }
}

//Set layer indexes in settings classes.
export function setSublayerIndexesInSettings(layer: __esri.MapImageLayer): void {
   Util.logMsg("Util_App.setSublayerIndexesInSettings");

   //Set the sublayer id's in the layer settings.
   for (const laySpec of Settings.layers) {
      if (laySpec.mapServiceID === layer.id) {
         const sublayer = Util_App.getSublayer(laySpec.layerName, layer.allSublayers);
         if (sublayer) {
            laySpec.layerIndex = sublayer.id;
         } else {
            laySpec.layerIndex = -1;
         }
      }
   }

   //Set the sublayer id's in the coded value domain settings for map image services.
   for (const codedValueSpec of Settings.codedValueSpecs) {
      if (codedValueSpec.layerName === layer.id) {
         codedValueSpec.sublayerIndexes = [];

         for (const sublayerName of codedValueSpec.sublayerNames) {
            const sublayer = Util_App.getSublayer(sublayerName, layer.allSublayers);
            if (sublayer) {
               codedValueSpec.sublayerIndexes.Add(sublayer.id);
            } else {
               codedValueSpec.sublayerIndexes.Add(-1);
            }
         }
      }
   }
   Util.logMsg("Done setting sublayer Indexes in Settings");
}

//Turn on a sublayer and all it's parent sublayers.
export function setSublayerVisible(subLayerName: string, sublayers: __esri.Collection<__esri.Sublayer>) {
   for (const sublayer of sublayers) {
      if (sublayer.title.toLowerCase() === subLayerName.toLowerCase()) {
         if (sublayer.layer.type !== "tile") {
            sublayer.visible = true;
            if (sublayer.parent && sublayer.parent.title.toLowerCase() !== subLayerName.toLowerCase()) {
               if (sublayer.parent instanceof Sublayer) {
                  setSublayerVisible(sublayer.parent.title, sublayers);
               }
            }
         }
      }
   }
}

//Sort feature class. Usage: myClassArray.sort(Util_App.sortFC("My_Property_Name"));
export function sortFC(property: string) {
   return function (a, b) {
      return (a.attributes[property] < b.attributes[property]) ? -1 : (a.attributes[property] > b.attributes[property]) ? 1 : 0;
   };
}

export function sortFC_Reverse(property: string) {
   return function (a, b) {
      return (a.attributes[property] > b.attributes[property]) ? -1 : (a.attributes[property] < b.attributes[property]) ? 1 : 0;
   };
}

//Compute 2 point distance.
export function twoPntDistance(pnt1: __esri.Point | __esri.ScreenPoint, pnt2: __esri.Point | __esri.ScreenPoint): number {
   const xD: number = Math.abs(pnt1.x - pnt2.x);
   const yD: number = Math.abs(pnt1.y - pnt2.y);
   const dist: number = Math.sqrt(Math.pow(xD, 2) + Math.pow(yD, 2));
   return dist;
}
